import { AdminListSubMenu, AdminStatus, OrgStatus, getAdminStatus } from '../../../../../utils/constants';
import { Box, IconButton, TableCell, TableRow, Typography, useMediaQuery, useTheme } from '@mui/material';

import { ADMIN_SUB_MENU_LIST } from '../../../../../utils/ui-constants';
import BinIcon from '../../../../../components/CustomIcons/BinIcon';
import DotsVerticalIcon from '../../../../../components/CustomIcons/DotsVerticalIcon';
import MenuData from '../../../../../types/ui/menu-data';
import MenuSelectDialog from '../../../../../components/MenuSelectDialog';
import Organization from '../../../../../types/organization';
import OrganizationAdmin from '../../../../../types/organization-admin';
import React from 'react';
import ReloadIcon from '../../../../../components/CustomIcons/ReloadIcon';
import Util from '../../../../../utils/util';
import useStyles from './styles';

interface Props {
  organization: Organization;
  admin: OrganizationAdmin;
  onResendInviteAction: () => void;
  onDeleteAction: () => void;
  isReadMode: boolean;
}

/**
 * Renders a table row displaying information for an organization administrator.
 * Includes status, name, login ID, and actions to resend invite or delete.
 *
 * @param {Props} props - The component's props.
 * @returns {JSX.Element} - The rendered table row.
 */
const AdminTableRow: React.FC<Props> = (props: Props) => {

  const { organization, admin } = props;
  const styles = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [subMenuAnchorEl, setSubMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  /**
   * Checks if the admin's status indicates they are not yet registered.
   *
   * @param {AdminStatus} status - The current status of the administrator.
   * @returns {boolean} - True if the admin is pending or in progress and the organization is active.
   */
  const isAdminNotRegistered = (status: AdminStatus) => {

    return (props.organization.details.status === OrgStatus.Active &&
      (status === AdminStatus.Pending || status === AdminStatus.InProgress));
  }

  const isAdminDeletable = (status: AdminStatus, registered: boolean) => {

    return (props.organization.details.status === OrgStatus.Draft &&
      (status === AdminStatus.Pending && !registered));
  }

  /**
   * Handles clicking the sub-menu icon to open the actions menu.
   *
   * @param {React.MouseEvent<HTMLButtonElement, MouseEvent>} event - The click event object.
   * @returns {void}
   */
  const onSubMenuClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.stopPropagation();
    setSubMenuAnchorEl(event.currentTarget);
  }

  /**
   * Handles selecting an action from the sub-menu (Resend Invite or Delete).
   *
   * @param {MenuData} menu - The data object representing the clicked sub-menu option.
   * @returns {void}
   */
  const handleSubMenuItemClick = (menu: MenuData) => {
    setSubMenuAnchorEl(null);
    if (menu.id === AdminListSubMenu.ResendInvite) {
      props.onResendInviteAction();
    } else {
      props.onDeleteAction();
    }
  }

  /**
   * Determines the list of sub-menu items to display based on the admin's status and organization details.
   *
   * @returns {MenuData[]} - An array of MenuData objects representing the available sub-menu items.
   */
  const getAdminSubMenu = () => {
    let menuList = ADMIN_SUB_MENU_LIST;
    if (!isAdminNotRegistered(admin.status)) {
      menuList = menuList.filter(item => item.id !== AdminListSubMenu.ResendInvite);
    }
    if (props.organization.details.status === OrgStatus.Active) {
      menuList = menuList.filter(item => item.id !== AdminListSubMenu.Delete);
    }

    return menuList;
  }

  const adminMenuList = getAdminSubMenu();

  return (
    <>
      {isMobile ? (
        <Box sx={styles.rowWrapper}>
          <Box sx={styles.statusWrapper}>
            <Box sx={styles.statusDot(admin.status)} />
          </Box>
          <Box sx={styles.contentWrapper}>
            <Typography variant='p1'>{admin.name}</Typography>
            <Typography variant='footer'>{admin.loginId}</Typography>
          </Box>
          {!Util.isArrayEmpty(adminMenuList) &&
            <IconButton onClick={onSubMenuClick}>
              <DotsVerticalIcon />
            </IconButton>
          }
        </Box>
      ) : (
        <TableRow>
          {(organization.details.status === OrgStatus.Active) &&
            <TableCell sx={styles.statusCell(admin.status)}>
              <Box sx={styles.statusDot(admin.status)} />
              {getAdminStatus().find(status => status.id === admin.status)?.value ?? ''}
            </TableCell>
          }
          <TableCell>{admin.name}</TableCell>
          <TableCell>{admin.loginId}</TableCell>
            <TableCell align='right'>
              {isAdminNotRegistered(admin.status) &&
                <ReloadIcon sx={styles.inviteIcon} onClick={props.onResendInviteAction} />}
            </TableCell>
            <TableCell align='right'>
              {isAdminDeletable(admin.status, admin.registered) && <IconButton
                onClick={props.onDeleteAction}
                disabled={props.isReadMode}
                size='small'
                sx={styles.binIconButton}
              >
                <BinIcon sx={styles.binIcon}/>
              </IconButton>
              }
            </TableCell>
          </TableRow>
      )}
      <MenuSelectDialog
        anchorEl={subMenuAnchorEl}
        open={Boolean(subMenuAnchorEl)}
        onClose={() => setSubMenuAnchorEl(null)}
        menuList={adminMenuList}
        onMenuItemSelected={handleSubMenuItemClick}
      />
    </>
  );
};

export default AdminTableRow;